import { useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/core/styles';
import Button from '../../shared/Button';
import { color } from '../../../app/theme';
import { uploadFile } from '../../../features/ecBindingSlice';
import logo from '../../../assets/images/png/img_linkmember_logo.png';

const useStyles = makeStyles(
  {
    root: {
      marginBottom: 36,
    },
    title: {
      fontStyle: 'normal',
      fontWeight: 500,
      fontSize: 15,
      lineHeight: '15px',
      color: color.$3,
      marginBottom: 8,
    },
    subTitle: {
      fontStyle: 'normal',
      fontWeight: 'normal',
      fontSize: 13,
      lineHeight: '18px',
      marginBottom: 12,
      color: '#4C4C4C',
    },
    download: {
      color: '#9cc100',
      cursor: 'pointer',
      textDecoration: 'underline',
    },
    block: {
      display: 'flex',
      alignItems: 'center',
    },
    button: {
      marginLeft: 8,
    },
    errorMsg: {
      fontSize: 13,
      fontWeight: 'normal',
      fontStretch: 'normal',
      fontStyle: 'normal',
      lineHeight: 1.46,
      marginLeft: 12,
      color: color.$17,
    },
  },
  {
    name: 'LogoImage',
  },
);

const LogoImage = ({ onChange, onReset }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const uploadInput = useRef();
  const [errorMsg, setErrorMsg] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const fileNameRegex = new RegExp(
    '^[\u4e00-\u9fa5A-Za-z0-9\u3001-\u3003\uff1f\uff0c\\w]+$',
  );

  function validateFileName(file) {
    const name = file?.name
      ? file.name.slice(0, file.name.lastIndexOf('.'))
      : '';
    return name && fileNameRegex.test(name);
  }

  const verifyImg = files => {
    const formats = ['jpg', 'jpeg', 'png'];

    if (files.length > 1) {
      setErrorMsg(t('image-file-count-error'));
      return false;
    }

    if (
      files.some(
        file =>
          !formats.some(format =>
            file.name.toLowerCase().endsWith(format.toLowerCase()),
          ),
      )
    ) {
      setErrorMsg(t('image-file-type-error'));

      return false;
    }

    if (files[0].size > 1000000) {
      setErrorMsg(t('image-file-size-error'));
      return false;
    }

    return new Promise(resolve => {
      const file = files[0];
      const reader = new FileReader();

      reader.addEventListener(
        'load',
        () => {
          const imgage = new Image();
          imgage.addEventListener('load', () => {
            const isFileVerify = validateFileName(file);
            const imgData = {
              name: file.name,
              src: imgage.src,
              file,
              width: imgage.width,
              height: imgage.height,
            };
            if (imgage.width === 250 && imgage.height === 250 && isFileVerify) {
              resolve(imgData);
            } else if (!isFileVerify) {
              setErrorMsg(t('image-name-error'));
              resolve(false);
            } else {
              setErrorMsg(t('image-size-logo-error'));
              resolve(false);
            }
          });
          imgage.src = window.URL.createObjectURL(file);
        },
        false,
      );

      reader.readAsDataURL(file);
    });
  };

  const uploadImg = async files => {
    try {
      const imgData = await verifyImg(files);
      if (imgData) {
        const url = await dispatch(uploadFile(imgData?.file));
        onChange(url);
        setErrorMsg('');
      }
    } catch (e) {
      setErrorMsg(t('image-error'));
    } finally {
      if (uploadInput?.current) {
        uploadInput.current.value = null;
      }
    }
  };

  const handleInputUpload = async e => {
    setIsLoading(true);
    const files = [...e.target.files];
    await uploadImg(files);
    setIsLoading(false);
  };

  return (
    <div className={classes.root}>
      <div className={classes.title}>{t('ecBinding:logoImage')}</div>
      <div className={classes.subTitle}>
        {t('ecBinding:logoImage-subtitle')}
        <a className={classes.download} href={logo} download>
          {t('sample')}
        </a>
      </div>
      <input
        type="file"
        ref={uploadInput}
        accept=".jpg,.jpeg,.png,.svg"
        hidden
        onChange={handleInputUpload}
      />
      <div className={classes.block}>
        <Button
          topic="primaryBlue"
          isLoading={isLoading}
          onClick={() => uploadInput.current.click()}
        >
          {t('uploadImg')}
        </Button>
        {!errorMsg ? (
          <Button
            className={classes.button}
            topic="primaryBlue"
            onClick={onReset}
          >
            {t('resetImg')}
          </Button>
        ) : (
          <div className={classes.errorMsg}>{errorMsg}</div>
        )}
      </div>
    </div>
  );
};

export default LogoImage;
